import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { Button, Dropdown, Grid, GridColumn, Icon } from "semantic-ui-react";

import { Can, DATE_FORMATS_FNS, NavPortal, SearchFilter, usePermanentRealtimeDealers } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { FilterDatePicker } from "modules/NewsDashboard/components";
import { setNewsFilters, useNewsFilters } from "modules/NewsDashboard/components/NewsDashboardFilters/hooks";
import "modules/NewsDashboard/components/NewsDashboardFilters/NewsDashboardFilters.scss";
import { useLanguageOptions, useNewsCategoriesQuery } from "modules/NewsDashboard/hooks";
import { NEWS_STORAGE_KEYS } from "modules/NewsDashboard/storageKeys";
import { setPreference } from "utils/common";

type NewsDashboardFiltersProps = {
  onOpenCreateModal: () => void;
};

export const NewsDashboardFilters = ({ onOpenCreateModal }: NewsDashboardFiltersProps) => {
  const { t } = useTranslation();

  const { data: categories } = useNewsCategoriesQuery();
  const { dealers } = usePermanentRealtimeDealers();
  const { dropdownOptions } = useLanguageOptions();
  const newsFilters = useNewsFilters();
  const { category_ids, dealer_ids, language_code, published_date, search_term } = newsFilters;

  const dealerOptions = dealers.map(({ id, name }) => ({ key: id, value: id, text: name }));
  const categoryOptions = categories?.map(cat => ({ key: cat.id, value: cat.id, text: t(`v8_${cat.name.toLowerCase()}`) || cat.name })) || [];

  const handleCategoryChange = (value: number[]) => {
    setPreference(NEWS_STORAGE_KEYS.CategoryPreference, value);
    setNewsFilters({ ...newsFilters, category_ids: value, page: 1 });
  };

  const handleDateChange = (date: Date | null) => {
    const published_date = date ? format(date, DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ) : null;
    setPreference(NEWS_STORAGE_KEYS.PublishedDatePreference, published_date);
    setNewsFilters({ ...newsFilters, published_date, page: 1 });
  };

  const handleSearchChange = (value: string) => {
    setNewsFilters({ ...newsFilters, search_term: value || null, page: 1 });
  };

  const handleLanguageChange = (languageCode: string) => {
    setPreference(NEWS_STORAGE_KEYS.LanguagePreference, languageCode);
    setNewsFilters({ ...newsFilters, language_code: languageCode, page: 1 });
  };

  const handleDealerChange = (value: number | null) => {
    setPreference(NEWS_STORAGE_KEYS.DealerPreference, value ? [value] : null);
    setNewsFilters({ ...newsFilters, dealer_ids: value ? [value] : null, page: 1 });
  };

  return (
    <>
      <NavPortal>
        <SearchFilter value={search_term || ""} handleSearchChange={handleSearchChange} />
      </NavPortal>

      <div className="NewsDashboardFilters">
        <Grid columns="equal" className="NewsDashboardFilters__container">
          <Can I={ACL_ACTIVITIES.Create} the={ACL_RESOURCES.Articles}>
            <Button icon onClick={onOpenCreateModal} className="add-article-button">
              <Icon className="plus" />
            </Button>
          </Can>

          <GridColumn width={4}>
            <Dropdown
              fluid
              selection
              search
              name="dealer"
              clearable
              selectOnBlur={false}
              value={dealer_ids?.[0] || ""}
              options={dealerOptions}
              placeholder={t("v8_filter_by_dealer", "Filter by dealer")}
              onChange={(_event, data) => handleDealerChange(data.value as number | null)}
            />
          </GridColumn>

          <GridColumn width={4}>
            <Dropdown
              fluid
              selection
              name="filterByCategory"
              clearable
              multiple
              selectOnBlur={false}
              value={category_ids || []}
              options={categoryOptions}
              placeholder={t("v8_filter_by_category", "Filter by category")}
              onChange={(_event, data) => handleCategoryChange(data.value as number[])}
            />
          </GridColumn>

          <GridColumn width={3}>
            <Dropdown
              fluid
              selection
              name="filterByLanguage"
              value={language_code}
              selectOnBlur={false}
              options={dropdownOptions}
              placeholder={t("v8_filter_by_language", "Filter by language")}
              onChange={(_event, data) => handleLanguageChange(data.value as string)}
            />
          </GridColumn>

          <GridColumn width={4}>
            <FilterDatePicker clearable value={published_date} onChange={handleDateChange} placeholder={t("v8_select_published_date", "Select published date")} />
          </GridColumn>
        </Grid>
      </div>
    </>
  );
};
